import fetchMock, { MockRequest } from 'fetch-mock'
import { parse } from 'query-string'
import XHRMock, { delay as xhrMockDelay, proxy } from 'xhr-mock'
import { GraphQLMock, HttpMethod, HttpMock, Mock, MockConfig, Operation, Scenarios } from './types'

/**
 * Gets the corresponding value for `scenario` key in the browser's Location object.
 */
export const extractScenarioFromLocation = (location: Location): string => {
  const { scenario = 'default' } = parse(location.search)
  if (Array.isArray(scenario)) {
    throw new Error('Only one scenario may be used at a time')
  }
  return scenario as string
}
interface ScenarioDataType {
  scenarios: Scenarios
  scenario: keyof Scenarios
  config?: MockConfig
}
interface ConfigType {
  scenarioData?: ScenarioDataType
  activeMocks: Array<string>
}

export const mockConfig: ConfigType = {
  scenarioData: undefined,
  activeMocks: [],
}

export const convertToString = (item: HttpMock) => {
  return `${item.url.toString()}:${item.method}:${item.body?.toString() ?? ''}`
}

/**
 * Orchestrator for setting up mocks.
 */
export const injectMocks = (
  scenarios: Scenarios,
  scenario: keyof Scenarios = 'default',
  config?: MockConfig,
  activeMocks: string | undefined = undefined,
): void => {
  XHRMock.setup()
  mockConfig.scenarioData = { scenarios, scenario, config }
  if (activeMocks === undefined) {
    mockConfig.activeMocks = scenarios.default.map((item) => convertToString(item as HttpMock))
  } else {
    mockConfig.activeMocks = activeMocks !== '' ? activeMocks.split(',') : []
  }

  fetchMock.config.fallbackToNetwork = config?.allowFetchPassthrough ?? true
  fetchMock.config.warnOnFallback = config?.disableConsoleWarningsForFetch ?? true

  const mocks: Mock[] = reduceAllMocksForScenario(scenarios, scenario)

  const mockLength = mockConfig?.scenarioData?.scenarios?.default?.length
  if (mockLength === undefined || mockLength === 0) {
    throw new Error('Unable to instantiate mocks')
  }

  const restMocks = mocks.filter((m) => m.method !== 'GRAPHQL') as HttpMock[]
  const graphQLMocks = mocks.filter((m) => m.method === 'GRAPHQL') as GraphQLMock[]

  restMocks.forEach(handleRestMock)
  graphQLMocks.forEach(handleGraphQLMock)

  if (config?.allowXHRPassthrough ?? true) {
    XHRMock.use(proxy)
  }
}

/**
 * Returns all mocks for a given scenarios + everything in the default mocks
 * that don't have a matching scenario mock.
 */
export const reduceAllMocksForScenario = (scenarios: Scenarios, scenario: keyof Scenarios): Mock[] => {
  if (scenario === 'default') {
    return scenarios.default.filter((item) => {
      return mockConfig.activeMocks.includes(convertToString(item as HttpMock))
    })
  }

  const defaultMocks = scenarios.default
  const scenarioMocks = scenarios[scenario]

  if (!scenarioMocks) {
    throw new Error(`No mocks found for scenario '${scenario}'`)
  }

  const mocks = scenarioMocks.concat(defaultMocks)

  const initialHttpMocks = mocks.filter(({ method }) => method !== 'GRAPHQL') as HttpMock[]
  const initialGraphQlMocks = mocks.filter(({ method }) => method === 'GRAPHQL') as GraphQLMock[]

  const httpMocksByUrlAndMethod = initialHttpMocks.reduce<Record<string, HttpMock>>((result, mock) => {
    const { url, method, body = '' } = mock
    if (!result[`${url.toString()}${method}${body.toString()}`]) {
      result[`${url.toString()}${method}${body.toString()}`] = mock
    }

    return result
  }, {})
  const httpMocks = Object.values(httpMocksByUrlAndMethod)

  const graphQlMocksByUrlAndOperations = initialGraphQlMocks.reduce<Record<string, Record<string, Operation>>>(
    (result, mock) => {
      const { url, operations } = mock

      const operationsByNameAndType: Record<string, Operation> = result[url.toString()] ? result[url.toString()] : {}

      operations.forEach((operation) => {
        // Always take the latest operation
        operationsByNameAndType[`${operation.operationName}${operation.type}`] = operation
      })

      result[url.toString()] = operationsByNameAndType
      return result
    },
    {},
  )
  const graphQlMocks = Object.entries(graphQlMocksByUrlAndOperations).map(([url, operationsByName]) => {
    return {
      method: 'GRAPHQL',
      url: RegExp(url.replace(/^\/(.*)\/$/, '$1')),
      operations: Object.values(operationsByName),
    }
  }) as GraphQLMock[]

  return (httpMocks as any)
    .concat(graphQlMocks)
    .filter((item: HttpMock) => mockConfig.activeMocks.includes(convertToString(item as HttpMock)))
}

/**
 * Mocks the right HTTP method for a REST mock.
 */
function handleRestMock({ method, url, body, response, responseCode = 200, responseHeaders, delay = 0 }: HttpMock) {
  const finalResponse = {
    body: response,
    status: responseCode,
    headers: responseHeaders,
  }
  const httpMethods: HttpMethod[] = ['GET', 'POST', 'PUT', 'DELETE']
  if (!httpMethods.includes(method)) {
    throw new Error(`Unrecognised HTTP method ${method} - please check your mock configuration`)
  }
  const fetchMatcher = (reqUrl: string, { body: reqBody, method: reqMethod = 'GET' }: MockRequest) => {
    const bodyMatch = body === undefined || body.test(reqBody?.toString() || '')
    return url.test(reqUrl) && reqMethod.toUpperCase() === method && bodyMatch
  }

  fetchMock.mock(fetchMatcher, () => addDelay(delay).then(() => finalResponse), {
    overwriteRoutes: false,
  })
  XHRMock.use((request, response) => {
    const bodyMatch = body === undefined || body.test(request.body())
    if (url.test(request.url().toString()) && request.method().toUpperCase() === method && bodyMatch) {
      return xhrMockDelay(finalResponse, delay)(request, response)
    }
    return undefined
  })
}

/**
 * Mocks the right HTTP method for a GraphQL mock.
 */
function handleGraphQLMock({ url, operations }: GraphQLMock) {
  const graphQLErrorResponse = { errors: [] }
  const findMockGet = (u: string) => {
    const parsedUrl = new URL(u)
    const operationName = parsedUrl.searchParams.get('operationName')
    return operations.find((o) => o.operationName === operationName)
  }
  const findMockPost = (postBody: any) => {
    const jsonBody = typeof postBody === 'string' ? JSON.parse(postBody) : postBody
    return operations.find(({ operationName }) => operationName === jsonBody.operationName)
  }
  const delayedResponse = (delay: number | undefined, response: object | string) => {
    return addDelay(delay ? delay : 0).then(() => response)
  }

  fetchMock.get(url, (u) => {
    const mock = findMockGet(u)

    if (!mock || mock?.type === 'mutation') {
      return graphQLErrorResponse
    }

    const finalResponse = {
      body: mock.response,
      status: mock.responseCode,
      headers: mock.responseHeaders,
    }

    return delayedResponse(mock.delay, finalResponse)
  })

  fetchMock.post(
    url,
    (_, { body }) => {
      const mock = findMockPost(body)

      if (!mock) {
        return graphQLErrorResponse
      }

      const finalResponse = {
        body: mock.response,
        status: mock.responseCode,
        headers: mock.responseHeaders,
      }

      return delayedResponse(mock.delay, finalResponse)
    },
    {
      overwriteRoutes: false,
    },
  )
}

/**
 * Adds delay (in ms) before resolving a promise.
 */
const addDelay = (delay: number) => new Promise((res) => setTimeout(res, delay))
